import { useAuth } from "@features/auth";
import { Footer } from "@features/core/components/footer";
import { sidenav as Sidenav } from "@features/core/components/sidenav";
import { topnav as Topnav } from "@features/core/components/topnav";
import { useBaseComponent } from "@shared/base/hooks/useBaseComponent";
import { X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { LoadingInterceptor } from "../../shared/api";
import { useToast } from "../../shared/hooks/use-toast";
// import { Toaster } from "sonner";
import { RoutePermissionGate } from "@features/core/components/RoutePermissionGate";
import { useQueryClient } from "@tanstack/react-query";
interface MainLayoutProps {
  error?: Error | null;
  onErrorDismiss?: () => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  error = null,
  onErrorDismiss,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const { renderBreadcrumbs } = useBaseComponent();

  const hideSideAndTopNav = [
    "/login",
    "/forgot-password",
    "/reset-password",
    "/cx/payments/create/process_payment",
    "/",
  ].includes(location.pathname);

  const { toast } = useToast();

  const queryClient = useQueryClient();

  // State to track last updated time
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isSidenavCollapsed, setIsSidenavCollapsed] = useState(true);
  const handleRefresh = () => {
    // Option 1: Invalidate everything (triggers refetch of all active queries)
    queryClient.invalidateQueries();

    // Update last updated time
    setLastUpdated(new Date());
  };

  // Set initial last updated time when component mounts
  useEffect(() => {
    setLastUpdated(new Date());
  }, []);

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
        action: (
          <button
            onClick={onErrorDismiss}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 p-0 text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        ),
      });
    }
  }, [error, toast, onErrorDismiss]);

  // Don't render anything if we're on a route that should hide the layout
  if (hideSideAndTopNav) {
    return <Outlet />;
  }

  return (
    <div className="relative min-h-screen">
      <div className="flex-none fixed top-0 z-50 w-full">
        <Topnav />
      </div>
      <div className="flex flex-1 min-h-0">
        <div className="flex min-h-screen pt-16">
          <Sidenav collapsed={isSidenavCollapsed} onToggle={() => setIsSidenavCollapsed((collapse) => !collapse)} />
        </div>

        <main
          className={`flex-1 flex flex-col min-w-0 pt-16 transition-all duration-300 overflow-hidden ${
            isSidenavCollapsed ? 'ml-16' : 'ml-64'
          }`}
        >
          <div className="flex-1 p-4 overflow-y-auto">
            {/* <Toaster position="top-center" richColors /> */}
            <div className="w-full">
              <div className="mb-4">{renderBreadcrumbs()}</div>

              {/* Add permission gate here for protected routes */}
              <RoutePermissionGate
                fallback={
                  <div className="flex flex-col items-center justify-center h-64 space-y-4">
                    <h2 className="text-xl font-semibold text-gray-700">
                      Access Denied
                    </h2>
                    <p className="text-gray-500">
                      You don't have permission to access this page.
                    </p>
                  </div>
                }
              >
                <Outlet />
              </RoutePermissionGate>

              <LoadingInterceptor />
            </div>
          </div>
        </main>
      </div>
      <div className="flex-none">
        <Footer />
      </div>
    </div>
  );
};

export default MainLayout;
